<script setup lang="ts">
import { userModule } from '@/store/user/module'
import { computed, onMounted, ref } from 'vue'
import UIKit from '@pocketprep/ui-kit'

const {
    ModalContainer,
    Modal,
    Radio,
    Textarea: PocketTextarea,
    Button: PocketButton,
    Icon,
} = UIKit

type TFeedback = {
    helpfulness: string
    trust: string
    customResponse: string
}

const emit = defineEmits<{
    submit: [feedback: TFeedback]
    close: []
}>()

const helpfulness = ref<typeof helpfulnessOptions[number] | null>(null)
const helpfulnessOptions = [
    { label: 'Not at all', value: 'Not at all' },
    { label: 'Somewhat', value: 'Somewhat' },
    { label: 'Completely', value: 'Completely' },
    { label: 'Not sure', value: 'Not sure' },
]
const trustability = ref<typeof trustabilityOptions[number] | null>(null)
const trustabilityOptions = [
    { label: 'Not at all', value: 'Not at all' },
    { label: 'Somewhat', value: 'Somewhat' },
    { label: 'Completely', value: 'Completely' },
    { label: 'Not sure', value: 'Not sure' },
]
const customResponse = ref('')
const dialogRef = ref<HTMLElement | null>(null)

onMounted(() => {
    dialogRef.value?.focus()
})

const isDarkMode = computed(() => {
    return userModule.state.settings.isDarkMode
})

const isSubmitDisabled = computed(() => {
    return !helpfulness.value || !trustability.value
})

const updateUserWebConfig = async () => {
    await userModule.actions.updateWebConfig({
        hasSeenTutorEngagementSurvey: true,
    })
}

const submitForm = async () => {
    const response = {
        helpfulness: helpfulness.value?.label || '',
        trust: trustability.value?.label || '',
        customResponse: customResponse.value,
    }

    await updateUserWebConfig()
    emit('submit', response)
}

const close = async () => {
    await updateUserWebConfig()
    emit('close')
}

</script>

<template>
    <Portal to="modal">
        <ModalContainer
            key="tutorEngagementSurvey"
            :is-dark-mode="isDarkMode"
            :show-close-button="true"
            @close="close"
        >
            <template #modal>
                <Modal
                    class="tutor-engagement-survey"
                    :show-close-button="true"
                    :is-dark-mode="isDarkMode"
                    @close="close"
                >
                    <div
                        ref="dialogRef"
                        class="tutor-engagement-survey__content"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="survey-title"
                        aria-describedby="survey-subtitle"
                        tabindex="-1"
                    >
                        <div class="tutor-engagement-survey__title-section">
                            <Icon
                                type="tutorChat"
                                class="tutor-engagement-survey__tutor-chat-icon"
                                aria-hidden="true"
                                v-dark
                            />
                            <h2
                                id="survey-title"
                                class="tutor-engagement-survey__title"
                            >
                                Thanks for trying out Tutor
                            </h2>
                            <div
                                id="survey-subtitle"
                                class="tutor-engagement-survey__subtitle"
                                v-dark
                            >
                                We’d love your feedback so we can keep improving.
                            </div>
                            <span
                                v-dark
                                aria-hidden="true"
                                class="tutor-engagement-survey__divider"
                            />
                        </div>
                        <div
                            class="tutor-engagement-survey__form"
                            role="group"
                            aria-label="Tutor feedback"
                        >
                            <Radio
                                v-model="helpfulness"
                                :data="helpfulnessOptions"
                                :is-dark-mode="isDarkMode"
                                class="tutor-engagement-survey__field tutor-engagement-survey__helpfulness-radio"
                                label="How much did your chat with Tutor help you understand
                                the question or concept you were working on?"
                                aria-required="true"
                            />
                            <Radio
                                v-model="trustability"
                                :data="trustabilityOptions"
                                :is-dark-mode="isDarkMode"
                                class="tutor-engagement-survey__field tutor-engagement-survey__trustability-radio"
                                label="How much did you trust Tutor to provide helpful guidance?"
                                aria-required="true"
                            />
                            <PocketTextarea
                                v-model="customResponse"
                                label="What made you feel that way about Tutor?"
                                class="tutor-engagement-survey__field tutor-engagement-survey__field-input"
                                :is-dark-mode="isDarkMode"
                                height="62"
                                width=""
                            />
                        </div>
                        <div class="tutor-engagement-survey__actions">
                            <PocketButton
                                class="tutor-engagement-survey__dismiss-btn"
                                :is-dark-mode="isDarkMode"
                                @click="close"
                                type="secondary"
                            >
                                Cancel
                            </PocketButton>
                            <PocketButton
                                class="tutor-engagement-survey__submit-btn"
                                :disabled="isSubmitDisabled"
                                :aria-disabled="isSubmitDisabled"
                                :is-dark-mode="isDarkMode"
                                @click="submitForm"
                            >
                                Submit
                            </PocketButton>
                        </div>
                    </div>
                </Modal>
            </template>
        </ModalContainer>
    </Portal>
</template>

<style lang="scss" scoped>
.tutor-engagement-survey {
    position: absolute;
    top: 80px;
    left: 50%;
    transform: translateX(-50%);

    @include breakpoint(black-bear) {
        top: 0;
        left: 0;
        transform: none;
        width: 100%;
        min-height: 100%;
    }

    &__content {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        width: 520px;
        box-sizing: border-box;
        padding: 24px 32px 32px 32px;

        @include breakpoint(black-bear) {
            margin: 0 auto;
            width: 100%;
            padding: 24px 24px 32px 24px;
        }
    }

    &__tutor-chat-icon{
        margin-bottom: 8px;
        color: $ash;
        width: 20px;

        &--dark {
            color: $cloudy;
        }
    }

    &__title-section {
        width: 100%;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 6px;
        box-sizing: border-box;
    }

    &__title {
        font-size: 18px;
        font-weight: 600;
        line-height: 25px;
        letter-spacing: 0.3px;
    }

    &__subtitle {
        color: $ash;
        text-align: center;
        font-size: 15px;
        font-weight: 500;
        line-height: 20px;
        margin-bottom: 18px;

        &--dark {
            color: $barely-background;
        }
    }

    &__divider {
        width: calc(100% + 64px);
        border-bottom: 1px solid $fog;

        @include breakpoint(black-bear) {
            width: calc(100% + 48px)
        }

        &--dark {
            color: $ash;
        }
    }

    &__form {
        margin-top: 24px;
        width: 100%;
    }

    &__actions {
        margin-top: 24px;
        align-self: center;
    }

    &__submit-btn {
        margin-left: 8px;
    }

    &__trustability-radio,
    &__helpfulness-radio {
        padding: 0 8px 0 8px;
        margin-bottom: 24px;


        :deep(.uikit-radio__list) {
            padding-left: 10px;
        }
    }

    &__trustability-radio :deep(.uikit-radio__label),
    &__helpfulness-radio :deep(.uikit-radio__label),
    &__field-input :deep(.uikit-textarea__label) {
        font-size: 15px;
        font-weight: 600;
        line-height: 20px;
        color: $brand-black;

        &.uikit-radio__label--focus,
        &.uikit-textarea__label--focus {
            color: $brand-blue;
        }

        &.uikit-radio__label--hover {
            color: $brand-black;
        }

        &.uikit-radio__label--dark,
        &.uikit-textarea__label--dark {
            color: $fog;

            &.uikit-radio__label--focus,
            &.uikit-textarea__label--focus {
                color: $banana-bread;
            }

            &.uikit-radio__label--hover {
                color: $fog;
            }
        }
    }

    :deep(.uikit-textarea__textarea) {
        resize: none;
    }
}
</style>
